import dynamic from 'next/dynamic';
import styled from 'styled-components';
import { useEffect, useState, Fragment } from 'react';
import { Trans } from 'react-i18next';

import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { useCountriesQuery } from 'store/api/countries';
import { useLocation } from 'shared/hooks/useLocation';
import { variableFont, variableFontRegular } from 'styles/theme/text';
import { ChangeShoppingRegionModal } from '../modals/ChangeShoppingRegionModal';

const TP = 'productTemplate.components.buyBar.GlobalPricingInfo';

const SitePreferencesModal = dynamic(
  () =>
    import('layout/footer/components/SitePreferencesModal').then((mod) => mod.SitePreferencesModal),
  { ssr: false },
);

export interface IGlobalPricingInfo {
  location:
    | 'new_buy_bar'
    | 'used_buy_bar'
    | 'used_pdp'
    | 'price_carousel'
    | 'offer_buy_bar'
    | 'buy_bar_mobile';
  className?: string;
  splitInTwoLines?: boolean;
}

export const GlobalPricingInfo = ({
  location,
  className,
  splitInTwoLines = false,
}: IGlobalPricingInfo) => {
  const { selectedCountryCode } = useLocation();
  const { data } = useCountriesQuery();
  const [isGlobalPricingModalOpen, setIsGlobalPricingModalOpen] = useState(false);
  const [isSitePreferencesModalOpen, setIsSitePreferencesModalOpen] = useState(false);
  const countries = data?.countries || [];
  const selectedCountry = countries?.find((country) => country?.isoCode === selectedCountryCode);
  const { name, globalPricingBodyCopy, globalPricingShortCopy, isoCode } = selectedCountry || {};
  const isGlobalPricingRegion = globalPricingBodyCopy && globalPricingShortCopy;
  let globalPricingShortCopyArr = [];

  const splitBy = splitInTwoLines ? `${name}.` : name;

  if (isGlobalPricingRegion) {
    globalPricingShortCopyArr = globalPricingShortCopy.split(splitBy);
    globalPricingShortCopyArr.splice(1, 0, splitBy);
  }

  useEffect(() => {
    if (isGlobalPricingModalOpen) {
      sendTrackingEvent('MODAL_OPEN_VIEW', {
        modal_name: 'import_duties',
      });
    }
  }, [isGlobalPricingModalOpen]);

  if (!name) {
    return null;
  }

  const handleNonGlobalPricingCountryClick = () => {
    setIsSitePreferencesModalOpen(true);
  };

  const handleCountryClick = () => {
    setIsGlobalPricingModalOpen(true);
    sendTrackingEvent('IMPORT_DUTIES_INFO_TAP', {
      region: isoCode,
      event_context: location,
    });
  };

  const handleCloseModal = () => {
    setIsGlobalPricingModalOpen(false);
  };

  const handleChangeRegionClick = () => {
    setIsSitePreferencesModalOpen(true);
  };

  return (
    <>
      <GlobalPricingInfo.Text
        data-qa="global_pricing_info_text"
        className={className}
        $isNewMobilePdp={location === 'buy_bar_mobile'}
      >
        {isGlobalPricingRegion ? (
          <GlobalPricingInfo.InnerDiv
            data-qa="shopping_region_text"
            $isNewMobilePdp={location === 'buy_bar_mobile'}
          >
            {globalPricingShortCopyArr.map((text, i) => {
              return text === splitBy ? (
                <GlobalPricingInfo.Region
                  data-qa="global_pricing_region_text"
                  key={text}
                  onClick={handleCountryClick}
                >
                  {splitBy}
                </GlobalPricingInfo.Region>
              ) : (
                <Fragment key={text}>
                  {splitInTwoLines && i !== 0 && <br />}
                  {text}
                </Fragment>
              );
            })}
          </GlobalPricingInfo.InnerDiv>
        ) : (
          <Trans
            i18nKey={`${TP}.shoppingRegion`}
            defaults="Your shopping region is <region>{region}</region>."
            values={{ region: name }}
            components={{
              region: <GlobalPricingInfo.Region onClick={handleNonGlobalPricingCountryClick} />,
            }}
          />
        )}
      </GlobalPricingInfo.Text>
      {isGlobalPricingModalOpen && (
        <ChangeShoppingRegionModal
          body={globalPricingBodyCopy}
          onCloseModal={handleCloseModal}
          onChangeRegionClick={handleChangeRegionClick}
        />
      )}
      {isSitePreferencesModalOpen && (
        <SitePreferencesModal
          isModalOpen={isSitePreferencesModalOpen}
          setIsModalOpen={setIsSitePreferencesModalOpen}
        />
      )}
    </>
  );
};

GlobalPricingInfo.Text = styled.div<{ $isNewMobilePdp: boolean }>`
  ${({ $isNewMobilePdp }) => ($isNewMobilePdp ? `text-align: left;` : `text-align: center;`)}
  margin: 16px 0 0;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    text-align: left;
    margin: 10px 0 0;
    white-space: nowrap;
  }
`;

GlobalPricingInfo.Region = styled.button`
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
`;

GlobalPricingInfo.InnerDiv = styled.div<{ $isNewMobilePdp: boolean }>`
  ${({ $isNewMobilePdp }) =>
    $isNewMobilePdp
      ? `${variableFont({ size: '9px', height: '12px', fontWeight: '400' })};`
      : `${variableFontRegular({ size: '10.5px', height: '14px' })};`}
`;
